<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DinastiaWhatsappAPI from 'dashboard/api/channel/dinastiaWhatsappChannel';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
      QUICK_REACTIONS,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      showDeleteForAllModal: false,
      showReactionPicker: false,
      showEditModal: false,
      editedContent: '',
      isReacting: false,
      isEditing: false,
      isDeleting: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
      currentChat: 'unifiedView/getEffectiveChat',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    inboxId() {
      return this.currentChat?.inbox_id;
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    handleReact() {
      this.showReactionPicker = !this.showReactionPicker;
    },
    async sendReaction(emoji) {
      this.showReactionPicker = false;
      this.handleClose();
      this.isReacting = true;

      // Get full message from Vuex store (payloadForContextMenu only has 4 fields)
      const chatMessages = this.currentChat?.messages || [];
      const fullMessage = chatMessages.find(m => m.id === this.messageId);

      if (fullMessage) {
        // Optimistic update: show reaction immediately using full message
        const previousReactions = [
          ...(fullMessage.content_attributes?.reactions || []),
        ];
        const optimisticReactions = previousReactions.filter(
          r => r.sender !== 'agent'
        );
        optimisticReactions.push({
          emoji,
          sender: 'agent',
          sender_name: 'Agent',
          timestamp: Math.floor(Date.now() / 1000),
        });
        this.$store.dispatch('updateMessage', {
          ...fullMessage,
          content_attributes: {
            ...fullMessage.content_attributes,
            reactions: optimisticReactions,
          },
        });
      }

      try {
        const { data } = await DinastiaWhatsappAPI.react(this.inboxId, {
          messageId: this.messageId,
          emoji,
        });
        if (data.message) {
          this.$store.dispatch('updateMessage', data.message);
        }
      } catch (error) {
        // Revert optimistic update on failure
        if (fullMessage) {
          this.$store.dispatch('updateMessage', fullMessage);
        }
        useAlert(this.$t('CONVERSATION.DINASTIA_ACTIONS.REACT_FAILED'));
      } finally {
        this.isReacting = false;
      }
    },
    handleEditMessage() {
      this.handleClose();
      this.editedContent = this.messageContent || '';
      this.showEditModal = true;
    },
    async confirmEdit() {
      if (!this.editedContent || this.editedContent === this.messageContent)
        return;

      this.isEditing = true;
      try {
        const { data } = await DinastiaWhatsappAPI.editMessage(this.inboxId, {
          messageId: this.messageId,
          content: this.editedContent,
        });
        // Update message in Vuex store so UI reflects the change immediately
        if (data?.message) {
          this.$store.dispatch('updateMessage', data.message);
        } else {
          this.$store.dispatch('updateMessage', {
            ...this.message,
            content: this.editedContent,
            content_attributes: {
              ...this.message.content_attributes,
              edited: true,
            },
          });
        }
        useAlert(this.$t('CONVERSATION.DINASTIA_ACTIONS.EDIT_SUCCESS'));
        this.showEditModal = false;
      } catch (error) {
        useAlert(this.$t('CONVERSATION.DINASTIA_ACTIONS.EDIT_FAILED'));
      } finally {
        this.isEditing = false;
      }
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    handleDeleteForAll() {
      this.handleClose();
      this.showDeleteForAllModal = true;
    },
    async confirmDeleteForAll() {
      this.isDeleting = true;
      try {
        const { data } = await DinastiaWhatsappAPI.deleteMessage(this.inboxId, {
          messageId: this.messageId,
        });
        // Update message in Vuex store so UI reflects the deletion immediately
        if (data?.message) {
          this.$store.dispatch('updateMessage', data.message);
        } else {
          this.$store.dispatch('updateMessage', {
            ...this.message,
            content_attributes: {
              ...this.message.content_attributes,
              deleted: true,
            },
          });
        }
        useAlert(this.$t('CONVERSATION.DINASTIA_ACTIONS.DELETE_ALL_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CONVERSATION.DINASTIA_ACTIONS.DELETE_ALL_FAILED'));
      } finally {
        this.isDeleting = false;
        this.showDeleteForAllModal = false;
      }
    },
    closeDeleteForAllModal() {
      this.showDeleteForAllModal = false;
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <!-- Confirm Delete For All -->
    <woot-delete-modal
      v-if="showDeleteForAllModal && enabledOptions['deleteForAll']"
      v-model:show="showDeleteForAllModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteForAllModal"
      :on-confirm="confirmDeleteForAll"
      :title="$t('CONVERSATION.DINASTIA_ACTIONS.DELETE_ALL_TITLE')"
      :message="$t('CONVERSATION.DINASTIA_ACTIONS.DELETE_ALL_MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <!-- Edit Message Modal -->
    <woot-modal
      v-if="showEditModal"
      v-model:show="showEditModal"
      :on-close="closeEditModal"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-3">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.EDIT_PROMPT') }}
        </h3>
        <textarea
          v-model="editedContent"
          class="w-full border border-n-weak rounded-lg p-3 min-h-[120px] bg-n-background text-n-slate-12 resize-y focus:outline-none focus:ring-2 focus:ring-n-brand"
          :placeholder="$t('CONVERSATION.REPLYBOX.PLACEHOLDER')"
          @keydown.ctrl.enter="confirmEdit"
          @keydown.meta.enter="confirmEdit"
        />
        <div class="flex justify-end gap-2 mt-4">
          <woot-button variant="clear" @click="closeEditModal">
            {{ $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL') }}
          </woot-button>
          <woot-button :is-loading="isEditing" @click="confirmEdit">
            {{ $t('CONVERSATION.DINASTIA_ACTIONS.EDIT_SAVE') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <hr />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="enabledOptions['react'] || enabledOptions['editMessage']" />
        <MenuItem
          v-if="enabledOptions['react']"
          :option="{
            icon: 'emoji',
            label: $t('CONVERSATION.CONTEXT_MENU.REACT'),
          }"
          variant="icon"
          @click.stop="handleReact"
        />
        <div
          v-if="showReactionPicker && enabledOptions['react']"
          class="flex items-center gap-1 px-2 py-1"
        >
          <div
            v-for="emoji in QUICK_REACTIONS"
            :key="emoji"
            role="button"
            class="text-lg hover:scale-125 transition-transform cursor-pointer p-0.5 select-none"
            :class="{
              'opacity-50 cursor-not-allowed pointer-events-none': isReacting,
            }"
            @mousedown.prevent.stop="sendReaction(emoji)"
          >
            {{ emoji }}
          </div>
        </div>
        <MenuItem
          v-if="enabledOptions['editMessage']"
          :option="{
            icon: 'edit',
            label: $t('CONVERSATION.CONTEXT_MENU.EDIT'),
          }"
          variant="icon"
          @click.stop="handleEditMessage"
        />
        <hr v-if="enabledOptions['delete'] || enabledOptions['deleteForAll']" />
        <MenuItem
          v-if="enabledOptions['deleteForAll']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE_FOR_ALL'),
          }"
          variant="icon"
          @click.stop="handleDeleteForAll"
        />
        <MenuItem
          v-if="enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
      </div>
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}

.context-menu--delete-modal {
  ::v-deep {
    .modal-container {
      @apply max-w-[30rem];

      h2 {
        @apply font-medium text-base;
      }
    }
  }
}
</style>
